import api from './api';
import {
  BookingOption,
  EventPromotionRow,
  BookingRequestResponse,
  BookingStatus,
  CreateBookingRequest,
  OrganizerKind,
  PageResponse,
  PriceItem,
  PriceItemRequest,
  PromotionTier,
} from '@radevents/shared';

/**
 * Preise und Buchungsanfragen.
 *
 * Bewusst ohne Zahlungsanbieter: eine Anfrage landet im Dashboard, die Rechnung entsteht
 * ausserhalb des Systems, und „übernehmen" schaltet die Leistung frei. Zweck ist zuerst zu
 * messen, ob und zu welchem Preis überhaupt jemand zahlen will.
 */
const bookingService = {
  /** Aktive Preise — auch ohne Anmeldung abrufbar, damit die Partnerseite sie zeigen kann. */
  publicPrices: async (): Promise<PriceItem[]> => {
    const response = await api.get('/prices');
    return response.data;
  },

  /**
   * Was gerade beworben wird — laufende, geplante und seit höchstens `expiredWithinDays` Tagen
   * abgelaufene Hervorhebungen, nach Ablauf sortiert.
   */
  promotions: async (expiredWithinDays = 30): Promise<EventPromotionRow[]> => {
    const response = await api.get('/admin/promotions', { params: { expiredWithinDays } });
    return response.data;
  },

  adminPrices: async (): Promise<PriceItem[]> => {
    const response = await api.get('/admin/prices');
    return response.data;
  },

  createPrice: async (data: PriceItemRequest): Promise<PriceItem> => {
    const response = await api.post('/admin/prices', data);
    return response.data;
  },

  updatePrice: async (id: number, data: PriceItemRequest): Promise<PriceItem> => {
    const response = await api.put(`/admin/prices/${id}`, data);
    return response.data;
  },

  /** Schaltet einen Preis ab. Gelöscht wird nie — Anfragen verweisen darauf. */
  deactivatePrice: async (id: number): Promise<void> => {
    await api.delete(`/admin/prices/${id}`);
  },

  // --- Veranstalter-Sicht ---------------------------------------------------------------

  options: async (
    organizerId: number,
    params: { eventId?: number; eventEditId?: number } = {},
  ): Promise<BookingOption[]> => {
    const response = await api.get(`/organizer-portal/${organizerId}/booking-options`, { params });
    return response.data;
  },

  create: async (organizerId: number, data: CreateBookingRequest): Promise<BookingRequestResponse> => {
    const response = await api.post(`/organizer-portal/${organizerId}/bookings`, data);
    return response.data;
  },

  myBookings: async (organizerId: number): Promise<BookingRequestResponse[]> => {
    const response = await api.get(`/organizer-portal/${organizerId}/bookings`);
    return response.data;
  },

  cancel: async (organizerId: number, bookingId: number): Promise<void> => {
    await api.post(`/organizer-portal/${organizerId}/bookings/${bookingId}/cancel`);
  },

  /**
   * Rechnung zu einer offenen Bestellung anfordern.
   *
   * Für Veranstalter, deren Buchhaltung ohne Beleg nicht überweist. Der Ablauf bleibt Vorkasse —
   * freigeschaltet wird weiter erst nach dem Zahlungseingang, nur der Beleg wandert nach vorn.
   * Ein zweiter Klick ist harmlos: liegt die Rechnung schon vor, kommt die Bestellung
   * unverändert zurück.
   */
  requestInvoice: async (organizerId: number, bookingId: number): Promise<BookingRequestResponse> => {
    const response = await api.post(`/organizer-portal/${organizerId}/bookings/${bookingId}/invoice`);
    return response.data;
  },

  // --- Redaktions-Sicht -----------------------------------------------------------------

  list: async (
    status?: BookingStatus,
    page = 0,
    size = 20,
  ): Promise<PageResponse<BookingRequestResponse>> => {
    const response = await api.get('/admin/bookings', { params: { status, page, size } });
    return response.data;
  },

  openCount: async (): Promise<{ count: number }> => {
    const response = await api.get('/admin/bookings/counts');
    return response.data;
  },

  setStatus: async (id: number, status: BookingStatus, comment?: string): Promise<BookingRequestResponse> => {
    const response = await api.post(`/admin/bookings/${id}/status`, { status, comment });
    return response.data;
  },

  /**
   * Rechnung für den Kunden anfordern — derselbe Vorgang wie sein eigener Knopf.
   *
   * Nötig, weil Kunden schreiben statt zu klicken: „wir brauchen eine Rechnung" kommt per Mail,
   * und ihn dafür zurück in den Veranstalter-Bereich zu schicken wäre genau der Umweg, den der
   * Knopf vermeiden soll.
   */
  requestInvoiceAsEditor: async (id: number): Promise<BookingRequestResponse> => {
    const response = await api.post(`/admin/bookings/${id}/invoice`);
    return response.data;
  },

  /**
   * Zahlung eingegangen: vermerken und freischalten — der Knopf des Alltags.
   *
   * Der Zahlungseingang bleibt auch dann stehen, wenn die Freischaltung scheitert (Event noch in
   * Prüfung); die Bestellung steht dann auf „Zahlung erhalten" statt zurückzufallen.
   */
  markPaid: async (
    id: number, comment?: string, tier?: PromotionTier, orgKind?: OrganizerKind,
  ): Promise<BookingRequestResponse> => {
    const response = await api.post(`/admin/bookings/${id}/paid`, { tier, comment, orgKind });
    return response.data;
  },

  /** Bezahlt, aber ohne Rechnung: nachholen. Scheitert es, kommt der Grund als 400. */
  retryPaidInvoice: async (id: number, orgKind?: OrganizerKind): Promise<BookingRequestResponse> => {
    const response = await api.post(`/admin/bookings/${id}/paid-invoice`, { orgKind });
    return response.data;
  },

  /**
   * Setzt die gebuchte Leistung: Hervorhebung bzw. Premium laufen ab jetzt.
   *
   * `tier` bleibt normalerweise leer — welche Stufe gesetzt wird, steht in der Anfrage, weil der
   * Veranstalter sie gewählt und bezahlt hat. Mitschicken heißt: bewusst abweichen.
   */
  apply: async (id: number, comment?: string, tier?: PromotionTier): Promise<BookingRequestResponse> => {
    const response = await api.post(`/admin/bookings/${id}/apply`, { tier, comment });
    return response.data;
  },
};

export default bookingService;
